import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ToastState as ts } from '../../../shared/enums/toast-state.enum';
import { BiometriaService } from '../../../shared/services/biometria.service';
import { ToastService } from '../../../shared/services/toast.service';
import { finalize } from 'rxjs';
import { ReactiveFormsModule } from '@angular/forms';
import * as faceapi from 'face-api.js';
import { Router } from '@angular/router';
import { LocalContextService } from '../../../shared/services/local-context.service';
import { RecebimentoService } from '../../../shared/services/recebimento.service';
import { SearchFaceRequest } from '../../../shared/models/biometria';
import { ControlPass } from '../../control-pass/models/control-pass';
import { DialogControlPassComponent } from '../../control-pass/dialog-control-pass/dialog-control-pass.component';
import { MatDialog } from '@angular/material/dialog';
import { ImpressoraService } from '../../../shared/services/impressora.service';

@Component({
  selector: 'app-biometria',
  imports: [ReactiveFormsModule],
  templateUrl: './biometria.component.html',
  styleUrl: './biometria.component.scss',
})
export class BiometriaComponent implements AfterViewInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;
  @ViewChild('faceGuide') faceGuideElement!: ElementRef<HTMLDivElement>;

  previewUrl: string | null = null;
  base64Image: string | null = null;
  isCameraActive: boolean = false;
  cameraStatusMessage: string = 'Preparando o reconhecimento facial...';
  isRecognitionError: boolean = false;

  private readonly FACE_NOT_RECOGNIZED_MESSAGE =
    'ROSTO NÃO RECONHECIDO. FAVOR GERAR UMA SENHA CLICANDO NO BOTÃO "NÃO CADASTRADO"';
  private readonly FACE_NOT_RECOGNIZED_DISPLAY_MS = 10000;
  private readonly UNCONFIGURED_TOTEM_MESSAGE =
    'Totem sem local configurado. Solicite ajuda ao atendimento.';
  private readonly GENERIC_OPERATION_ERROR_MESSAGE =
    'Não foi possível concluir a operação. Tente novamente ou procure o atendimento.';

  private mediaStream: MediaStream | null = null;
  private currentLocalId: number | null = null;

  private modelsLoaded: boolean = false;
  private faceStabilityCount: number = 0;
  private readonly REQUIRED_STABILITY_FRAMES = 20;
  private detectionTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private detectionLoopRunning: boolean = false;
  private destroyed: boolean = false;

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly TEMPO_LIMITE_INATIVIDADE_MS = 45000;

  get isFaceAligned(): boolean {
    return this.faceStabilityCount > 0;
  }

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private biometriaService: BiometriaService,
    private recebimentoService: RecebimentoService,
    private impressoraService: ImpressoraService,
    private localContextService: LocalContextService,
    private toast: ToastService,
    private cdr: ChangeDetectorRef,
  ) {
    this.localContextService.currentLocalId$.subscribe((id) => {
      if (id) this.currentLocalId = id;
    });
  }

  async ngAfterViewInit(): Promise<void> {
    this.inactivityTimer();
    await this.loadFaceApiModels();

    if (!this.destroyed) {
      await this.startCamera();
    }
  }

  private inactivityTimer() {
    this.clearTimer();
    this.timeoutId = setTimeout(() => {
      this.toast.showToast(
        ts.warning,
        'Tempo esgotado. Sessão encerrada por inatividade.',
      );
      this.returnHome();
    }, this.TEMPO_LIMITE_INATIVIDADE_MS);
  }

  private clearTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  returnHome() {
    this.stopCamera();
    this.clearTimer();
    this.router.navigate(['/totem/home']);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopCamera();
    this.clearTimer();
    this.isCameraActive = false;
  }

  async loadFaceApiModels(): Promise<void> {
    try {
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      this.modelsLoaded = true;
    } catch (error) {
      this.modelsLoaded = false;
      const message = this.getErrorMessage(
        error,
        'Erro ao carregar o reconhecimento facial.',
      );
      this.cameraStatusMessage = message;
      this.toast.showToast(ts.error, message);
    }
  }

  async startCamera(): Promise<void> {
    if (!this.modelsLoaded || this.destroyed) return;

    this.isRecognitionError = false;
    this.cameraStatusMessage = 'Por favor, aguarde ...';
    this.stopCamera();

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new DOMException(
          'O navegador não disponibiliza acesso à câmera neste contexto.',
          'NotSupportedError',
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 960 },
          height: { ideal: 720 },
          aspectRatio: { ideal: 4 / 3 },
        },
      });

      if (this.destroyed) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const video = this.videoElement.nativeElement;
      this.mediaStream = stream;
      video.srcObject = stream;
      await video.play();

      this.isCameraActive = true;
      this.cameraStatusMessage = 'Posicione seu rosto dentro da marcação oval.';
      this.startDetectionLoop();
    } catch (error) {
      this.stopCamera();
      const message = this.getCameraErrorMessage(error);
      this.cameraStatusMessage = message;
      this.toast.showToast(ts.error, message);
    }
  }

  startDetectionLoop(): void {
    if (this.detectionLoopRunning || !this.isCameraActive || this.destroyed) {
      return;
    }

    this.detectionLoopRunning = true;
    void this.detectFace();
  }

  async detectFace(): Promise<void> {
    if (!this.isCameraActive || !this.videoElement || this.destroyed) {
      this.detectionLoopRunning = false;
      return;
    }

    const video = this.videoElement.nativeElement;

    if (video.paused || video.ended || video.videoWidth === 0) {
      this.scheduleNextDetection();
      return;
    }

    try {
      const detection = await faceapi.detectSingleFace(
        video,
        new faceapi.TinyFaceDetectorOptions({
          inputSize: 224,
          scoreThreshold: 0.8,
        }),
      );

      if (detection) {
        this.inactivityTimer();
        const guidanceMessage = this.getFaceGuidanceMessage(
          detection.box,
          video,
        );

        if (guidanceMessage) {
          this.faceStabilityCount = 0;
          this.cameraStatusMessage = guidanceMessage;
        } else {
          this.faceStabilityCount++;

          const percentage = Math.min(
            (this.faceStabilityCount / this.REQUIRED_STABILITY_FRAMES) * 100,
            100,
          );
          this.cameraStatusMessage = `Posição correta! Mantenha-se imóvel ... ${percentage.toFixed(0)}%`;

          if (this.faceStabilityCount >= this.REQUIRED_STABILITY_FRAMES) {
            this.isCameraActive = false;
            this.captureFrame();
            this.onSave();
            return;
          }
        }
      } else {
        this.faceStabilityCount = 0;
        this.cameraStatusMessage =
          'Posicione seu rosto dentro da marcação oval.';
      }

      this.cdr.detectChanges();
    } catch (error) {
      this.faceStabilityCount = 0;
      this.isCameraActive = false;
      const message = this.getErrorMessage(
        error,
        'Falha ao analisar a imagem da câmera.',
      );
      this.cameraStatusMessage = message;
      this.toast.showToast(ts.error, message);
      this.cdr.detectChanges();
      this.stopCamera();
      return;
    }

    this.scheduleNextDetection();
  }

  captureFrame(): void {
    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const context = canvas.getContext('2d');

    if (context && video.videoWidth > 0 && video.videoHeight > 0) {
      const videoRect = video.getBoundingClientRect();
      const viewportWidth =
        videoRect.width || video.clientWidth || video.videoWidth;
      const viewportHeight =
        videoRect.height || video.clientHeight || video.videoHeight;
      const crop = this.calculateVisibleCrop(
        video.videoWidth,
        video.videoHeight,
        viewportWidth,
        viewportHeight,
      );

      canvas.width = Math.round(crop.width);
      canvas.height = Math.round(crop.height);

      context.drawImage(
        video,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        canvas.width,
        canvas.height,
      );

      const base64DataUrl = canvas.toDataURL('image/jpeg', 0.8);
      this.previewUrl = base64DataUrl;
      this.base64Image = base64DataUrl;
      this.cameraStatusMessage = 'Foto capturada automaticamente!';
      this.stopCamera();
    }
  }

  stopCamera(): void {
    if (this.detectionTimeoutId) {
      clearTimeout(this.detectionTimeoutId);
      this.detectionTimeoutId = null;
    }
    this.detectionLoopRunning = false;

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }

    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }

    this.isCameraActive = false;
  }

  resetCamera(): void {
    this.previewUrl = null;
    this.base64Image = null;
    this.faceStabilityCount = 0;
    this.isRecognitionError = false;

    if (!this.destroyed) {
      void this.startCamera();
    }
  }

  private scheduleNextDetection(): void {
    if (!this.isCameraActive || this.destroyed) {
      this.detectionLoopRunning = false;
      return;
    }

    this.detectionTimeoutId = setTimeout(() => {
      this.detectionTimeoutId = null;
      void this.detectFace();
    }, 100);
  }

  private calculateVisibleCrop(
    videoWidth: number,
    videoHeight: number,
    viewportWidth: number,
    viewportHeight: number,
  ): { x: number; y: number; width: number; height: number } {
    const videoAspectRatio = videoWidth / videoHeight;
    const viewportAspectRatio = viewportWidth / viewportHeight;

    if (videoAspectRatio > viewportAspectRatio) {
      const width = videoHeight * viewportAspectRatio;
      return {
        x: (videoWidth - width) / 2,
        y: 0,
        width,
        height: videoHeight,
      };
    }

    const height = videoWidth / viewportAspectRatio;
    return {
      x: 0,
      y: (videoHeight - height) / 2,
      width: videoWidth,
      height,
    };
  }

  private getFaceGuidanceMessage(
    box: { x: number; y: number; width: number; height: number },
    video: HTMLVideoElement,
  ): string | null {
    const videoRect = video.getBoundingClientRect();
    const guideRect =
      this.faceGuideElement.nativeElement.getBoundingClientRect();

    if (
      videoRect.width === 0 ||
      videoRect.height === 0 ||
      guideRect.width === 0 ||
      guideRect.height === 0
    ) {
      return null;
    }

    const crop = this.calculateVisibleCrop(
      video.videoWidth,
      video.videoHeight,
      videoRect.width,
      videoRect.height,
    );
    const scaleX = videoRect.width / crop.width;
    const scaleY = videoRect.height / crop.height;
    const faceCenterX =
      videoRect.left +
      videoRect.width -
      (box.x + box.width / 2 - crop.x) * scaleX;
    const faceCenterY =
      videoRect.top + (box.y + box.height / 2 - crop.y) * scaleY;
    const renderedFaceWidth = box.width * scaleX;
    const renderedFaceHeight = box.height * scaleY;
    const guideCenterX = guideRect.left + guideRect.width / 2;
    const guideCenterY = guideRect.top + guideRect.height / 2;

    if (
      renderedFaceWidth < guideRect.width * 0.45 ||
      renderedFaceHeight < guideRect.height * 0.4
    ) {
      return 'Aproxime o rosto da câmera.';
    }

    if (
      renderedFaceWidth > guideRect.width * 0.95 ||
      renderedFaceHeight > guideRect.height * 0.95
    ) {
      return 'Afaste um pouco o rosto da câmera.';
    }

    const isHorizontallyCentered =
      Math.abs(faceCenterX - guideCenterX) <= guideRect.width * 0.18;
    const isVerticallyCentered =
      Math.abs(faceCenterY - guideCenterY) <= guideRect.height * 0.18;

    if (!isHorizontallyCentered || !isVerticallyCentered) {
      return 'Centralize o rosto dentro da marcação oval.';
    }

    return null;
  }

  private getCameraErrorMessage(error: unknown): string {
    const errorName =
      typeof error === 'object' && error && 'name' in error
        ? String(error.name)
        : '';

    switch (errorName) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Permissão da câmera negada. Libere o acesso no navegador.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'Nenhuma câmera foi encontrada neste equipamento.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'A câmera está ocupada por outro aplicativo.';
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return 'A câmera não suporta a configuração solicitada.';
      case 'SecurityError':
        return 'O acesso à câmera foi bloqueado por segurança.';
      case 'NotSupportedError':
        return this.getErrorMessage(
          error,
          'Este navegador não permite acessar a câmera.',
        );
      default:
        return this.getErrorMessage(
          error,
          'Não foi possível acessar a câmera.',
        );
    }
  }

  private getErrorMessage(_error: unknown, fallback: string): string {
    return fallback;
  }

  onSave() {
    if (!this.base64Image) return;

    this.cameraStatusMessage = 'Analisando biometria no sistema, aguarde...';
    this.cdr.detectChanges();

    const dto: SearchFaceRequest = { base64Image: this.base64Image };

    this.biometriaService
      .searchForMatchingFace(dto)
      .pipe(finalize(() => {}))
      .subscribe({
        next: (result) => {
          if (result.success && result.content?.driver) {
            this.isRecognitionError = false;
            const cpfReconhecido = result.content.driver.cpfMot;
            this.cameraStatusMessage = `Acesso Liberado, ${result.content.driver.nomeMot}!`;
            this.cdr.detectChanges();

            setTimeout(() => {
              this.generatePassAndInitalizeReceiving(cpfReconhecido);
            }, 1000);
          } else {
            this.showFaceNotRecognizedMessage();
          }
        },
        error: (error) => {
          if (this.isFaceNotRecognizedError(error)) {
            this.showFaceNotRecognizedMessage();
            return;
          }

          this.cameraStatusMessage = this.GENERIC_OPERATION_ERROR_MESSAGE;
          this.toast.showToast(ts.error, this.GENERIC_OPERATION_ERROR_MESSAGE);
          this.cdr.detectChanges();
          setTimeout(() => this.returnHome(), 3500);
        },
      });
  }

  private showFaceNotRecognizedMessage() {
    this.isRecognitionError = true;
    this.cameraStatusMessage = this.FACE_NOT_RECOGNIZED_MESSAGE;
    this.toast.showToast(ts.error, this.FACE_NOT_RECOGNIZED_MESSAGE);
    this.cdr.detectChanges();
    setTimeout(
      () => this.generatePassAndInitalizeReceiving(null),
      this.FACE_NOT_RECOGNIZED_DISPLAY_MS,
    );
  }

  private isFaceNotRecognizedError(error: any): boolean {
    const messages = Array.isArray(error?.error)
      ? error.error
      : error?.error?.messages || [];

    return messages.some(
      (message: unknown) =>
        typeof message === 'string' &&
        message.toLocaleLowerCase().includes('nenhuma face correspondente'),
    );
  }

  private generatePassAndInitalizeReceiving(driverId?: string | null) {
    if (this.currentLocalId === null) {
      this.cameraStatusMessage = this.UNCONFIGURED_TOTEM_MESSAGE;
      this.toast.showToast(ts.error, this.UNCONFIGURED_TOTEM_MESSAGE);
      this.cdr.detectChanges();
      setTimeout(() => this.returnHome(), 5000);
      return;
    }

    const localId = this.currentLocalId;

    this.cameraStatusMessage = 'Imprimindo sua senha...';
    this.cdr.detectChanges();

    const payload = { localId, driverId: driverId ?? null };

    this.recebimentoService.generatePassAndReceiving(payload).subscribe({
      next: (result) => {
        if (result.success) {
          this.printGeneratedPass(result.content, localId);
        }
      },
      error: () => {
        this.toast.showToast(ts.error, this.GENERIC_OPERATION_ERROR_MESSAGE);
        setTimeout(() => this.returnHome(), 3000);
      },
    });
  }

  private printGeneratedPass(item: ControlPass, localId: number): void {
    this.impressoraService.printTicket(item.senha, localId).subscribe({
      next: () => {
        this.toast.showToast(
          ts.success,
          'Senha gerada e impressa com sucesso!',
          'CONTROL PASS',
        );
        this.openDialogDetails(item);
      },
      error: () => {
        this.toast.showToast(
          ts.warning,
          `Sua senha é ${item.senha}. A impressão não foi concluída. Anote o número e procure o atendimento.`,
          'CONTROL PASS',
        );
        this.openDialogDetails(item);
      },
    });
  }

  private openDialogDetails(item: ControlPass) {
    const dialogRef = this.dialog.open(DialogControlPassComponent, {
      data: item,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      this.returnHome();
    });

    setTimeout(() => {
      dialogRef.close();
    }, 8000);
  }
}
